/**
 * Presigned-upload Dropzone initializers for reupload, collection cover, brief
 * attachments and newsletter images.
 *
 * These replace the legacy multipart Dropzone POSTs to PHP (`reupload.php`,
 * `coverupload.php`, `brief-upload.php`, `nlupload.php`). Dropzone's own uploader
 * is disabled (`autoProcessQueue: false`); each file runs the presign → PUT →
 * complete pipeline so the bytes go straight to S3, bypassing the web server.
 */

import { api } from "../api";
import { registerActions } from "../actionRegistry";
import { showLoadingOverlay, hideLoadingOverlay, toastError } from "../ui/messages";
import { getDomElement } from "../utils/dom";
import Dropzone from "dropzone";
import { putWithProgress, downscaleJpeg, cropSquareJpeg } from "./uploadShared";
import { briefAddFile, ieclientAddFile } from "./uploadCallbacks";
import { coverdone } from "./collections";
import { nlUploadDone } from "./newsletter";

/**
 * Attach a Dropzone configured as a drag/drop + click affordance with its own
 * uploader disabled. The provided `handle` runs the full pipeline per file.
 */
function attachUploadDropzone(
  elementId: string,
  acceptedFiles: string,
  maxFilesize: number,
  handle: (file: File) => void | Promise<void>
): void {
  const el = getDomElement(elementId);
  if (!el) return;

  new Dropzone(el, {
    url: "#",
    autoProcessQueue: false,
    maxFilesize,
    acceptedFiles,
    createImageThumbnails: false,
    previewTemplate: "",
    addedfile: (file: DropzoneFile) => {
      void handle(file);
    },
    accept: (_file: DropzoneFile, done: (error?: string) => void) => {
      done();
    },
  });
}

// ── collection cover ────────────────────────────────────────────────

/**
 * `collId` is passed rather than read from `#collection_id`, which used to be
 * emitted by BOTH `views/pages/Collection.tsx` and `EditCollectionModal` — and
 * `#modals` precedes the page content in `Chrome.tsx`, so on a collection page
 * the modal's copy shadowed the page's for every reader of that id, not just
 * this one. Only one of them emits it now.
 */
export function initCoverUpload(collId: string, onSuccess?: () => void): void {
  attachUploadDropzone("coverupload", "image/jpeg", 100, async (file) => {
    if (!collId) {
      toastError("Missing collection");
      return;
    }
    if (file.type !== "image/jpeg") {
      toastError("Only JPEG files are allowed");
      return;
    }

    showLoadingOverlay("Uploading...");
    let key: string;
    try {
      const data = await api.cover.uploadPresign();
      key = data.key;
      await putWithProgress(data.url, file, (pct) => {
        showLoadingOverlay(`Uploading... ${pct}% done`);
      });
    } catch (err) {
      hideLoadingOverlay();
      toastError((err as Error).message || "Upload failed");
      return;
    }

    try {
      await api.cover.uploadComplete({ key, id: collId });
      hideLoadingOverlay();
      coverdone();
      if (onSuccess) onSuccess();
    } catch (err) {
      hideLoadingOverlay();
      toastError((err as Error).message || "Upload failed");
    }
  });
}

// ── brief attachments ───────────────────────────────────────────────

export function initBriefUpload(briefId: string, onSuccess?: () => void): void {
  attachUploadDropzone("brief-fileupload", ".jpg,.jpeg,.png,.tif,.tiff,.pdf", 100, async (file) => {
    // One tile per file, held by reference for the life of this closure — the
    // four callbacks used to re-find it from `hashCode(file.name)` on every call.
    const tile = briefAddFile(file.name);

    let key: string;
    try {
      const data = await api.briefs.uploadPresign({ briefId, filename: file.name });
      key = data.key;
      await putWithProgress(data.url, file, (pct) => {
        tile.progress(pct);
      });
    } catch (err) {
      tile.failed((err as Error).message);
      return;
    }

    try {
      const result = await api.briefs.uploadComplete({ briefId, key, filename: file.name });
      tile.ready(result);
      if (onSuccess) onSuccess();
    } catch (err) {
      tile.failed((err as Error).message);
    }
  });
}

// ── newsletter image ────────────────────────────────────────────────

export function initNlImageUpload(): void {
  attachUploadDropzone("newsletter-image-button", "image/jpeg", 100, async (file) => {
    if (file.type !== "image/jpeg") {
      toastError("Only JPG uploads are allowed");
      return;
    }

    showLoadingOverlay("Uploading... 0% done");
    try {
      const blob = await downscaleJpeg(file, 1000);
      const data = await api.newsletter.imageUploadPresign();
      await putWithProgress(data.url, blob, (pct) => {
        showLoadingOverlay(`Uploading... ${pct}% done`);
      });
      nlUploadDone(data.publicUrl);
    } catch (err) {
      hideLoadingOverlay();
      toastError((err as Error).message || "Upload failed");
    }
  });
}

// ── profile picture ─────────────────────────────────────────────────

/**
 * Crop → presign → PUT → complete pipeline for a profile picture. Returns the
 * stored S3 path (imgid). `userId` lets an admin set another user's picture.
 */
export async function uploadProfilePic(
  file: File,
  userId?: number
): Promise<{ imgid: string; name: string }> {
  if (file.type !== "image/jpeg") {
    throw new Error(file.name + ": only jpeg allowed");
  }

  const blob = await cropSquareJpeg(file, 400);
  const data = await api.users.profilePicPresign();
  await putWithProgress(data.url, blob, () => {
    // progress shown by callers as needed
  });
  return await api.users.profilePicComplete({ key: data.key, userId });
}

/**
 * Attach the profile-picture presigned pipeline to an element. `onSuccess`
 * receives the stored image path (imgid).
 */
export function initProfilePicUpload(
  elementId: string,
  opts: { userId?: number; onSuccess?: (imgid: string) => void } = {}
): void {
  attachUploadDropzone(elementId, "image/jpeg", 100, async (file) => {
    showLoadingOverlay("Uploading...");
    try {
      const result = await uploadProfilePic(file, opts.userId);
      hideLoadingOverlay();
      if (opts.onSuccess) opts.onSuccess(result.imgid);
    } catch (err) {
      hideLoadingOverlay();
      toastError((err as Error).message || "Upload failed");
    }
  });
}

// ── IE-client interior upload ───────────────────────────────────────

/**
 * Replaces the legacy multipart Dropzone POST to `ieclient-upload.php`. Each
 * file runs presign → PUT-to-S3 → complete (the complete step invokes the
 * resize-image Lambda), so the raw bytes go straight to S3. The per-file tile,
 * progress and success/error UI reuse the existing ieclient callbacks.
 */
export function initIeClientUpload(): void {
  attachUploadDropzone("ieclient-uploader", "image/jpeg", 50, async (file) => {
    if (file.type !== "image/jpeg") {
      toastError("Only JPEG files are allowed");
      return;
    }

    const tile = ieclientAddFile(file.name);

    let uuid: string;
    try {
      const data = await api.mockups.ieClientUploadPresign({ filename: file.name });
      uuid = data.uuid;
      await putWithProgress(data.url, file, (pct) => {
        tile.progress(pct);
      });
    } catch (err) {
      tile.failed((err as Error).message || "Upload failed");
      return;
    }

    try {
      const res = await api.mockups.ieClientUploadComplete({ uuid, filename: file.name });
      tile.ready(res);
    } catch (err) {
      tile.failed((err as Error).message || "Processing failed");
    }
  });
}

// The /mockup page triggers the ieclient uploader via `data-init-fn="initIeClientUpload"`
// (the ieclient modal calls it directly from modalPostLoad).
registerActions({
  initIeClientUpload,
});
